import { ENTRY, SAFE, YARD, isBranch, isHome } from "./types.js";
import { walk } from "./path.js";
import { areFriendly } from "./validation.js";
export const occupants = (state, cell) => state.tokens.filter((token) => token.cell === cell);
export function areAllies(state, first, second) {
    return areFriendly(state, first, second);
}
/** A blockade is exactly two friendly tokens on a shared ring cell. */
export function isBlock(state, cell) {
    if (!state.rules.blocks || cell === YARD || isBranch(cell) || isHome(cell))
        return false;
    const stack = occupants(state, cell);
    return stack.length === 2 && areAllies(state, stack[0]?.color ?? 0, stack[1]?.color ?? 0);
}
function cannotLand(state, cell, mover) {
    if (isBranch(cell) || isHome(cell))
        return false;
    const landing = occupants(state, cell);
    if (!landing.length)
        return false;
    if (isBlock(state, cell))
        return true;
    const enemies = landing.filter((token) => !areAllies(state, token.color, mover));
    const allies = landing.filter((token) => areAllies(state, token.color, mover));
    if (SAFE.has(cell))
        return !state.rules.safeCellsShareable && enemies.length > 0;
    if (enemies.length > 1 || allies.length > 1 || (enemies.length > 0 && allies.length > 0))
        return true;
    return false;
}
function crossesBlock(state, path) {
    return path.some((cell) => isBlock(state, cell));
}
/**
 * Returns only complete legal moves. It is a pure projection and can safely be
 * used by AI and UI hints; the reducer remains the authoritative validator.
 */
export function legalMoves(state, die = state.die ?? 0) {
    if (!Number.isInteger(die) || die < 1 || die > 6)
        return [];
    const mine = state.tokens.filter((token) => token.color === state.turn);
    const moves = [];
    for (const token of mine) {
        if (isHome(token.cell))
            continue;
        if (token.cell === YARD) {
            if (die !== state.rules.entryRoll)
                continue;
            const destination = ENTRY[state.turn];
            if (!cannotLand(state, destination, state.turn)) {
                const move = buildMove(state, token, destination, [destination], true);
                if (move)
                    moves.push(move);
            }
            continue;
        }
        const path = walk(state.turn, token.cell, die);
        if (!path || crossesBlock(state, path))
            continue;
        const destination = path.at(-1);
        if (destination === undefined || cannotLand(state, destination, state.turn))
            continue;
        const move = buildMove(state, token, destination, path, false);
        if (move)
            moves.push(move);
    }
    return moves;
}
function buildMove(state, token, to, path, exitsYard) {
    const landing = occupants(state, to);
    const enemies = landing.filter((other) => !areAllies(state, other.color, token.color));
    const allies = landing.filter((other) => areAllies(state, other.color, token.color));
    if (!SAFE.has(to) && !isBranch(to) && !isHome(to) && enemies.length > 1)
        return null;
    const captures = !SAFE.has(to) && !isBranch(to) && !isHome(to) ? enemies.map((other) => other.id) : [];
    return {
        tokenId: token.id,
        from: token.cell,
        to,
        path,
        captures,
        finishes: isHome(to),
        entersBranch: isBranch(to) && !isBranch(token.cell),
        exitsYard,
        formsBlock: state.rules.blocks && allies.length === 1 && captures.length === 0 && !isBranch(to) && !isHome(to),
    };
}
/**
 * Generates all legal two-dice sequences for the current state. Each sequence is
 * an ordered array of single-die moves that the player may perform using the
 * available dice in either order. This is a pure projection used by AI and
 * higher-level UI — the reducer remains the authoritative validator.
 */
export function legalSequences(state) {
    const dice = state.dice ?? (state.die ? [state.die] : []);
    if (!dice || dice.length === 0)
        return [];
    // Work with exactly two dice where possible; if only one die present, fall back
    const considerDice = dice.length >= 2 ? [dice[0], dice[1]] : [dice[0]];
    const sequences = [];
    const activeDie = state.die ?? considerDice[0];
    const pushIfUnique = (seq) => {
        const equalMove = (a, b) => a.tokenId === b.tokenId && a.from === b.from && a.to === b.to && a.captures.length === b.captures.length && a.captures.every((id, idx) => id === b.captures[idx]) && a.path.length === b.path.length && a.path.every((cell, idx) => cell === b.path[idx]);
        if (!sequences.some((s) => s.order.join(",") === seq.order.join(",") && s.moves.length === seq.moves.length && s.moves.every((m, i) => equalMove(m, seq.moves[i])))) {
            sequences.push(seq);
        }
    };
    // Helper to simulate a single move and return a preview state (tokens updated only)
    const previewApply = (s, move) => {
        const tokens = s.tokens.map((token) => {
            if (token.id === move.tokenId)
                return { ...token, cell: move.to };
            if (move.captures.includes(token.id))
                return { ...token, cell: YARD };
            return token;
        });
        return { ...s, tokens, lastMoved: { ...s.lastMoved, [s.turn]: move.tokenId } };
    };
    // Generate sequences using the active die first only. This mirrors the reducer's
    // behavior: if the active die has no moves, the reducer will pass the turn, so
    // do not propose alternative single-die sequences using the second die.
    const generateForOrder = (firstDie, secondDie) => {
        if (firstDie !== activeDie)
            return; // only consider sequences starting with the active die
        const firstMoves = legalMoves(state, firstDie);
        if (firstMoves.length === 0) {
            // Reducer would pass the turn — do not propose any sequence
            return;
        }
        for (const m1 of firstMoves) {
            const after1 = previewApply(state, m1);
            if (secondDie === undefined) {
                pushIfUnique({ order: [firstDie], moves: [m1] });
                continue;
            }
            const secondMoves = legalMoves(after1, secondDie);
            if (secondMoves.length === 0) {
                // Valid sequence: use first die only
                pushIfUnique({ order: [firstDie, secondDie], moves: [m1] });
                continue;
            }
            for (const m2 of secondMoves)
                pushIfUnique({ order: [firstDie, secondDie], moves: [m1, m2] });
        }
    };
    if (considerDice.length === 1) {
        generateForOrder(considerDice[0]);
        return sequences;
    }
    const [a, b] = considerDice;
    // Only generate sequences starting with the active die. Do not generate the reverse order.
    generateForOrder(a, b);
    return sequences;
}
